import {
	DocumentEntityType,
	DocumentsListProps,
	ProcessedDocumentItem,
} from "@dune/types";
import { Badge, ScrollArea } from "@dune/ui";
import {
	formatDueDate,
	getDueDateColor,
	getEntityFilterMatch,
	getEntityNavigationUrl,
} from "@dune/utils";
import { File05, Hourglass01 } from "@untitled-ui/icons-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetDuneDocuments } from "../../../api/queries/dashboard.queries";
import { useDocumentStore } from "../../../stores/document.store";

export const DocumentsList: React.FC<DocumentsListProps> = ({
	activeFilter,
}) => {
	const { data: documentsData, isLoading } = useGetDuneDocuments();
	const navigate = useNavigate();
	const setFileId = useDocumentStore((state) => state.setFileId);

	if (isLoading) {
		return (
			<div className="p-4 text-center text-gray-500">
				Chargement des documents...
			</div>
		);
	}

	const allDocuments = documentsData?.documents || [];

	const documents = allDocuments.filter((document) =>
		getEntityFilterMatch(document.entityType, activeFilter, document),
	);

	const handleDocumentClick = (document: ProcessedDocumentItem) => {
		const fallbackId =
			document.entityType === DocumentEntityType.SOCIETY && !document.entityId
				? (document.reference.match(/\d+/)?.[0] ?? "")
				: document.entityId;
		if (!fallbackId) {
			setFileId(document.id);
			return;
		}
		const url = getEntityNavigationUrl(document.entityType, fallbackId);
		navigate(url);
	};

	return (
		<ScrollArea className="h-full">
			<div className="space-y-3">
				{documents.map((document: ProcessedDocumentItem) => (
					<button
						key={document.id}
						type="button"
						className="w-full p-3 bg-white border border-gray-100 rounded-lg hover:border-blue-200 cursor-pointer transition-colors text-left"
						onClick={() => handleDocumentClick(document)}
					>
						<div className="flex items-start justify-between">
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-1">
									<span className="text-sm font-medium text-gray-900">
										{document.type}
									</span>
									<Badge
										variant="outline"
										className="text-xs text-orange-badge-text bg-orange-badge-background border-orange-badge-border"
									>
										{document.status}
									</Badge>
								</div>
								<div className="text-sm text-gray-600 mb-2">
									{document.description}
								</div>
								<div className="flex items-center gap-1 text-xs">
									<Hourglass01 className="h-4 w-4" />
									<span className={getDueDateColor(document.dueDate)}>
										{formatDueDate(document.dueDate)}
									</span>
								</div>
							</div>
							<div className="flex items-center gap-1 text-sm text-gray-500">
								<File05 className="h-4 w-4" />
								<span>{document.fileCount}</span>
							</div>
						</div>
					</button>
				))}
				{documents.length === 0 && (
					<div className="p-4 text-center text-gray-500">
						{activeFilter === "tous"
							? "Aucun document à traiter"
							: `Aucun document à traiter dans la catégorie "${activeFilter}"`}
					</div>
				)}
			</div>
		</ScrollArea>
	);
};
